import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '../../models/classes/location';
import { LocationFilter } from '../../models/classes/location.filter';
import { OTObject } from '../../models/classes/object';
import { AssetsService } from '../../services/asset.service';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs';
import { NavButtonComponent } from '../nav-button/nav-button';
import { FormsModule } from '@angular/forms';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LP_UI_CONFIG } from '../../core/tokens';
import { SafeSelectComponent, SafeSelectOption } from '../safe-select/safe-select';

type ToggleableFlags = 'Flange' | 'Tank' | 'Pipe' | 'Valve' | 'SteamTrap' | 'RotatingEquipment';

class FilterSelection {
  public values: Location[] = [];
  public selected: Location | null = null;
  public name = '';
  public disabled = false;
  constructor(
    public comp: FilterComponent,
    public index: number
  ) {}

  set Selected(selected: Location | null) {
    this.selected = selected;
    this.comp.setLocation(this.index, selected);
  }

  get Selected(): Location | null {
    return this.selected;
  }
}

@Component({
  selector: 'app-filter',
  imports: [
    NavButtonComponent,
    BreadcrumbsComponent,
    NzSelectModule,
    NzBadgeModule,
    NzIconModule,
    FormsModule,
    SafeSelectComponent,
  ],
  templateUrl: './filter.html',
  styleUrls: ['./filter.scss'],
})
export class FilterComponent {
  hasFlanges = true;
  hasPipes = true;
  hasValves = true;
  hasSteamTraps = true;
  hasTanks = true;
  hasRotatingEquipment = true;
  // ============================================================
  // DEPENDENCY INJECTION
  // ============================================================
  public readonly cfg = inject(LP_UI_CONFIG);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  private assetsService = inject(AssetsService);
  private router = inject(Router);

  // ============================================================
  // INPUTS & OUTPUTS
  // ============================================================
  submit_btn = input<boolean>(false);
  plantsFilter = input<boolean>(false);
  notificationsFilter = input<boolean>(false);
  countryOrRegionSelected = input<number>(0);
  closeFilter = output<void>();
  onChangeFilter = output<void>();

  // ============================================================
  // CORE STATE SIGNALS
  // ============================================================
  public filter = signal<LocationFilter>(new LocationFilter());
  public selectionPreset = signal<Location[]>([]);
  public loading = signal<boolean>(false);
  private notificationsCount = signal<number>(0);
  public empty = signal<Location>(new Location(new OTObject(), [], []));

  public hasWarning = signal<boolean>(true);
  public hasCritical = signal<boolean>(true);
  public hasInactive = signal<boolean>(true);
  public hasWorking = signal<boolean>(true);
  public hasDeInstalled = signal<boolean>(true);

  /** Track initialization to prevent effect loops */
  private initialized = signal<boolean>(false);
  readonly plantFilterStatus = input<number | null>(null);

  /** Track last synced filter to prevent redundant updates */
  private lastSyncedFilterJson = '';

  // ============================================================
  // COMPUTED: Source Data from AssetsService
  // ============================================================

  /** Unfiltered regions state - for filter options (shows ALL available options) */
  private unfilteredRegionsState = computed(() => this.assetsService.unfilteredRegionsState());

  /** Plants state from the store - reactive source */
  private plantsState = computed(() => this.assetsService.plantsState());

  /** Unfiltered regions with assets (layer 'a') - used to show ALL filter options */
  public unfiltered = computed(() => {
    const regions = this.unfilteredRegionsState().regions;
    const notifications = this.assetsService.getNotificationsInstantly();
    if (!!this.notificationsFilter() && notifications.length > 0) {
      return regions
        .map((item) => item.ApplyNotificationFilter(notifications))
        .filter((item) => !!item);
    }
    return regions.filter(
      (region: Location) => !!region && region.curLayer === 'a' && region.Assets.length > 0
    );
  });

  /** Plants filtered to layer 'c' */
  public plants = computed(() => {
    return this.plantsState().plants.filter((plant: Location) => plant.curLayer === 'c');
  });

  /** Filter count from service */
  public filterCount = computed(() => {
    return this.assetsService.FilterSettingsCount;
  });

  /** Is mobile view */
  public isMobile = computed(() => this.ui.IsMobile());

  // ============================================================
  // COMPUTED: Temp Filter for Calculations
  // ============================================================

  /** Create a temp filter for derived calculations */
  private tempFilter = computed(() => {
    const f = this.filter();
    const preset = this.selectionPreset();
    const isPlantsFilter = this.plantsFilter();

    const temp = new LocationFilter();
    temp.selectedTypes = f.selectedTypes;
    temp.warning = f.warning;
    temp.inactive = f.inactive;
    temp.critical = f.critical;
    temp.deInstalled = f.deInstalled;
    temp.location = f.location;
    temp.preselection = f.preselection;

    if (!isPlantsFilter) {
      temp.customer = f.customer;
    }

    // Initially, the location filter is empty
    if (temp.location.length < temp.preselection) {
      temp.location = preset.map((item) => item.objectID);
    }

    return temp;
  });

  /** Filtered items based on temp filter */
  private filteredItems = computed(() => {
    const temp = this.tempFilter();
    return this.unfiltered().map((item: any) => item.ApplyFilter(temp));
  });

  /** Assets from filtered items */
  public filteredAssets = computed(() => {
    return this.filteredItems()
      .map((item: any) => (item ? item.Assets : []))
      .flat(2);
  });

  // ============================================================
  // COMPUTED: Equipment Type Flags
  // ============================================================

  initializeEquipmentFilters() {
    this.hasFlanges = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'flange'
    );

    this.hasPipes = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'pipe'
    );

    this.hasValves = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'valve'
    );

    this.hasSteamTraps = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'steam trap'
    );

    this.hasTanks = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'tank'
    );

    this.hasRotatingEquipment = this.filteredAssets().some(
      (item: any) => item.equipmentType.toLowerCase() === 'rotating equipment'
    );
  }

  // ============================================================
  // COMPUTED: Customer Filter
  // ============================================================

  /** Check if user is super user (has multiple customers) */
  public isSuperUser = computed(() => {
    if (this.plantsFilter()) return false;

    const customerNames = this.unfiltered()
      .map((item: any) => (item ? item.Assets : []))
      .flat(2)
      .map((item: any) => item.customerName);

    const uniqueCustomers = [...new Set(customerNames)];
    return uniqueCustomers.length > 1;
  });

  /** Customer list for dropdown */
  public customerList = computed(() => {
    if (this.plantsFilter()) return [];

    const f = this.filter();

    // Create temp filter without customer to get all possible customers
    const temp = new LocationFilter();
    temp.selectedTypes = f.selectedTypes;
    temp.warning = f.warning;
    temp.inactive = f.inactive;
    temp.critical = f.critical;
    temp.deInstalled = f.deInstalled;
    temp.customer = null;

    // Don't apply location filter if it's only preset
    if (f.location.length <= f.preselection) {
      temp.location = [];
    } else {
      temp.location = f.location;
    }

    const filtered = this.unfiltered().map((item: any) => item.ApplyFilter(temp));
    const assets = filtered.map((item: any) => (item ? item.Assets : [])).flat(2);

    const customers = assets.map((item: any) => item.customerName);
    const uniqueCustomers = [...new Set(customers)].sort();

    return uniqueCustomers;
  });

  /** Current customer selection */
  public customer = computed(() => {
    const list = this.customerList();
    const f = this.filter();
    if (list.length === 1) return list[0];
    if (!f.customer) return 'null';
    return f.customer;
  });

  /** Is customer select disabled */
  public customerSelectDisabled = computed(() => this.customerList().length < 2);

  // ============================================================
  // COMPUTED: Location Filter Selections
  // ============================================================

  public filterSelections = computed(() => {
    const f = this.filter();
    const preset = this.selectionPreset();
    const unfilteredRegions = this.unfiltered();
    const isPlantsFilter = this.plantsFilter();
    const isEM = this.cfg.appName === 'em';

    // Create temp filter without location for initial filtering
    const temp = new LocationFilter();
    temp.selectedTypes = f.selectedTypes;
    temp.warning = f.warning;
    temp.inactive = f.inactive;
    temp.critical = f.critical;
    temp.working = f.working;
    temp.deInstalled = f.deInstalled;
    temp.location = [];
    temp.preselection = f.preselection;

    if (!isPlantsFilter) {
      temp.customer = f.customer;
    }

    let filtered = unfilteredRegions
      .map((item: any) => item.ApplyFilter(temp))
      .filter((item: any) => !!item);

    if (!isEM) {
      filtered = filtered.filter(
        (item: any) =>
          (!item.filtered && item.children.length > 0) || (item.filtered && item.Sensors.length > 0)
      );
    }

    const selections: FilterSelection[] = [];
    let lastValues = filtered;

    for (let i = 0; (i <= f.location.length || i <= preset.length) && lastValues.length > 0; i++) {
      const values = lastValues.filter((item: any) =>
        isEM
          ? true
          : (item.curLayer === 'b' && item.children.length > 0) ||
            (item.curLayer !== 'b' &&
              (!item.filtered || (item.filtered && item.Sensors.length > 0)))
      );

      const selection = preset[i]
        ? preset[i]
        : f.location[i]
          ? values.find((item: Location) => item.objectID === f.location[i])
          : undefined;

      selections.push(
        Object.assign(new FilterSelection(this, i), {
          values: values,
          selected: selection ? selection : this.empty(),
          name: getChildrenLayerNames(values, this.i18n),
          disabled: preset.length > i,
        })
      );

      if (!selection) break;
      lastValues = selection.children;
    }

    return selections;
  });

  // ============================================================
  // COMPUTED: Status & Asset Count
  // ============================================================

  public status = computed(() => {
    const f = this.filter();
    if (f.deInstalled) return 5;
    if (f.inactive) return 4;
    if (f.critical) return 3;
    if (f.warning) return 2;
    if (f.working) return 1;
    return 0;
  });

  public statusOptions = computed<SafeSelectOption[]>(() => [
    { value: 0, label: this.i18n.langString('all_status') },
    { value: 1, label: this.i18n.langString('working'), disabled: !this.hasWorking() },
    { value: 2, label: this.i18n.langString('warning'), disabled: !this.hasWarning() },
    { value: 3, label: this.i18n.langString('critical'), disabled: !this.hasCritical() },
    { value: 4, label: this.i18n.langString('inactive'), disabled: !this.hasInactive() },
    {
      value: 5,
      label: this.i18n.langString('deinstalled'),
      disabled: !this.hasDeInstalled(),
    },
  ]);

  public customerOptions = computed<SafeSelectOption[]>(() => [
    { value: 'null', label: this.i18n.langString('all_customers') },
    ...this.customerList().map((c) => ({ value: c, label: c })),
  ]);

  public locationOptionsFor(selection: FilterSelection): SafeSelectOption[] {
    return [
      { value: this.empty(), label: this.i18n.langString('region') },
      ...this.sortAreas(selection.values).map((v) => ({
        value: v,
        label: this.i18n.parse(v.objectName),
      })),
    ];
  }

  public AssetCount = computed(() => {
    if (this.filterCount() > 0) {
      if (this.notificationsFilter()) {
        return this.notificationsCount();
      }
      return this.plants()
        .map((plant: any) => plant.Assets)
        .flat(2)
        .map((asset: any) => asset.sensors)
        .flat(2).length;
    }
    return -1;
  });

  public isFiltered = computed(() => {
    const f = this.filter();
    return f.IsFiltering && (!this.plantsFilter() || !f.IsOnlyFilteringCustomer);
  });

  // ============================================================
  // CONSTRUCTOR: Effects for Side Effects
  // ============================================================
  private equipmentFiltersInitialized = signal(false);

  constructor() {
    effect(
      () => {
        const plants = this.plants();
        if (!this.equipmentFiltersInitialized() && plants.length > 0) {
          untracked(() => this.initializeEquipmentFilters());
          this.equipmentFiltersInitialized.set(true);
        }
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const f = this.filter();
      this.onChangeFilter.emit();
    });
    effect(() => {
      if (this.notificationsFilter() && this.cfg.appName === 'em') {
        this.assetsService.subscribeToNotifications((result) => {
          this.notificationsCount.set(result.notifications.length);
        });
      }
    });

    effect(() => {
      if (this.cfg.appName === 'em') {
        const val = this.plantFilterStatus();
        if (val != null && val > 0 && this.status() !== val) {
          // same behavior as: if changed AND val>0 => set Status
          this.setStatus(val);
        }
      }
    });

    // Effect 1: Initialize filter when regions are loaded
    effect(() => {
      const regionsState = this.unfilteredRegionsState();

      if (regionsState.loadingComplete && regionsState.regions.length > 0) {
        const isInitialized = untracked(() => this.initialized());

        if (!isInitialized) {
          untracked(() => {
            // Get initial filter from service
            const serviceFilter = this.assetsService.getFilter();
            if (serviceFilter) {
              this.filter.set(
                new LocationFilter({
                  ...serviceFilter,
                  countryOrRegionSelected: this.countryOrRegionSelected(),
                })
              );
            }

            // Get selection preset from service
            const preset = this.assetsService.getSelectionPreset();
            this.selectionPreset.set(preset);

            this.initialized.set(true);
          });
        }
      }
    });

    // Effect 2: Handle countryOrRegionSelected input changes
    effect(() => {
      const selected = this.countryOrRegionSelected();
      const isInitialized = untracked(() => this.initialized());
      const currentValue = untracked(() => this.filter().countryOrRegionSelected);

      // Only update if initialized and value actually changed
      if (isInitialized && selected !== currentValue) {
        untracked(() => {
          this.filter.update((f) => {
            return new LocationFilter({
              ...f,
              location: [],
              countryOrRegionSelected: selected,
            });
          });
        });
      }
    });

    // Effect 3: Sync filter to service when filter changes (not in submit mode)
    effect(() => {
      const f = this.filter();
      const isInitialized = untracked(() => this.initialized());
      const isSubmitMode = untracked(() => this.submit_btn());

      if (isInitialized && !isSubmitMode) {
        // Create a simple JSON representation to detect actual changes
        const filterJson = JSON.stringify({
          selectedTypes: f.selectedTypes,
          customer: f.customer,
          location: f.location,
          critical: f.critical,
          inactive: f.inactive,
          warning: f.warning,
          working: f.working,
          deInstalled: f.deInstalled,
        });

        // Only sync if the filter actually changed
        if (filterJson !== this.lastSyncedFilterJson) {
          this.lastSyncedFilterJson = filterJson;
          untracked(() => {
            this.assetsService.updateFilter(new LocationFilter(f));
          });
        }
      }
    });

    // Effect 4: Keep local selectionPreset in sync with store
    // (handles dynamic preset changes, e.g. when parent computes a new preset after customer filter change)
    effect(() => {
      const storePreset = this.assetsService.selectionPreset();
      const isInitialized = untracked(() => this.initialized());

      if (isInitialized) {
        const currentIds = untracked(() => this.selectionPreset())
          .map((item: Location) => item.objectID)
          .join(';');
        const newIds = storePreset.map((item: Location) => item.objectID).join(';');

        if (currentIds !== newIds) {
          this.selectionPreset.set(storePreset);
          // Also update preselection in local filter to match
          this.filter.update(
            (f) =>
              new LocationFilter({
                ...f,
                preselection: storePreset.length,
              })
          );
        }
      }
    });
  }

  // ============================================================
  // PUBLIC METHODS: Actions
  // ============================================================

  filterAssets(input: Location[], notifications: Notification[], filter: LocationFilter): any[] {
    if (this.notificationsFilter()) {
      notifications = this.applyNotificationFilter(input, notifications, filter);

      return input
        .map((item) => (item ? item.Assets : []))
        .flat(2)
        .filter((item) => !!notifications.find((noti: any) => noti.objectID === item.objectID));
    } else {
      const filtered = input.map((item) => item.ApplyFilter(filter));

      return filtered.map((item) => (item ? item.Assets : [])).flat(2);
    }
  }

  applyNotificationFilter(
    input: Location[],
    notifications: Notification[],
    filter: LocationFilter
  ): Notification[] {
    // filter logic for notifications:
    //  - first, determine assets which would pass the current filtering options, while ignoring any filtering for status
    //    at the moment
    //  - then filter notifications by checking if their asset is part of the previously determined assets
    //  - finally, filter notifications by the status options in the filter

    // create LocationFilter object without status filtering:
    const assetFilter = new LocationFilter(filter);
    assetFilter.inactive = false;
    assetFilter.working = false;
    assetFilter.warning = false;
    assetFilter.critical = false;
    assetFilter.deInstalled = false;

    // determine assets which would pass the current filtering options (-status filter)
    const assets: any[] = input
      .map((item: Location) => item.ApplyFilter(assetFilter))
      .filter((item) => !!item)
      .map((item: Location) => (item ? item.Assets : []))
      .flat(2);

    // return only those notifications which belong to the previously filtered assets
    let outp = notifications
      .map((notification: any) => {
        const asset = assets.find((_asset: any) => _asset.objectID === notification.objectID);
        if (!asset) return null;
        notification.breadcrumbs = asset.breadcrumbs;
        return notification;
      })
      .filter((item) => !!item)
      .filter((item) =>
        item.equipmentType === 'Rotating Equipment' ? true : item.notificationStatus !== 'repaired'
      );

    if (
      filter.critical ||
      filter.warning ||
      filter.inactive ||
      filter.working ||
      filter.deInstalled
    ) {
      outp = outp.filter(
        (notification: any) =>
          (filter.critical && notification.isCritical && !notification.inactive) ||
          (filter.warning && notification.isWarning) ||
          (filter.inactive && notification.inactive) ||
          (filter.working &&
            !notification.isCritical &&
            !notification.isWarning &&
            !notification.inactive) ||
          (filter.deInstalled && notification.deInstalled)
      );
    }

    return outp.sort((a: any, b: any) =>
      a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0
    );
  }

  public SelectionPreset = () => this.selectionPreset();

  public setSelectionPreset(preset: Location[]): void {
    this.selectionPreset.set(preset);
  }

  public toggleFilterFlag(flag: ToggleableFlags): void {
    this.filter.update((f) => {
      const updated = new LocationFilter({ ...f });
      updated[flag] = !updated[flag];
      return updated;
    });
  }

  filterStatus(assets: any) {
    this.hasInactive.set(!!assets.find((item: any) => item.inactive === true));
    this.hasCritical.set(
      !!assets.find((item: any) => item.critical === true && item.inactive === false)
    );
    this.hasWarning.set(
      !!assets.find(
        (item: any) => item.warning === true && item.inactive === false && item.critical === false
      )
    );
    this.hasWorking.set(
      !!assets.find(
        (item: any) => item.inactive === false && item.critical === false && item.warning === false
      )
    );
    this.hasDeInstalled.set(
      !!assets.find(
        (item: any) =>
          item.inactive === false &&
          item.critical === false &&
          item.warning === false &&
          item.isArchived === true
      )
    );
  }

  public setStatus = (val: number): void => {
    this.filter.update((f) => {
      return new LocationFilter({
        ...f,
        deInstalled: val === 5,
        inactive: val === 4,
        critical: val === 3,
        warning: val === 2,
        working: val === 1,
      });
    });
  };

  public setCustomer = (customer: string | null): void => {
    if ((!!customer && !customer.length) || customer === 'null') {
      customer = null;
    }

    this.filter.update((f) => {
      return new LocationFilter({
        ...f,
        customer: customer,
      });
    });
  };

  public setLocation = (index: number, value: Location | null): void => {
    const preset = this.selectionPreset();
    const f = this.filter();

    // Don't allow changing preset locations
    if (preset[index]) return;

    // Handle empty selection
    if (value === this.empty() || !value) {
      value = null;
    }

    // Get the current location ID at this index
    const currentLocationId = f.location[index];
    const newLocationId = value?.objectID;

    // Only update if the value actually changed
    if (newLocationId === currentLocationId) return;

    this.filter.update((currentFilter) => {
      const newLocation = [
        ...preset.map((item) => item.objectID),
        ...currentFilter.location.slice(0, index).slice(preset.length),
        ...(value ? [value.objectID] : []),
      ];

      return new LocationFilter({
        ...currentFilter,
        location: newLocation,
      });
    });
  };

  public clearFilter = (): void => {
    const currentCountryOrRegion = this.filter().countryOrRegionSelected;
    this.onChangeFilter.emit();
    this.filter.set(
      new LocationFilter({
        selectedTypes: [],
        customer: null,
        location: [],
        critical: false,
        inactive: false,
        warning: false,
        working: false,
        countryOrRegionSelected: null,
        deInstalled: false,
      })
    );

    if (currentCountryOrRegion) {
      this.router.navigate(['home', 'list']);
    }
  };

  public onSubmit(): void {
    this.assetsService.updateFilter(this.filter());
    this.closeFilter.emit();
  }

  public clearAllFilters(event: Event): void {
    this.assetsService.clearFilter();
    event.stopPropagation();
  }

  // ============================================================
  // UTILITY METHODS
  // ============================================================

  public sortAreas(areas: Location[]): Location[] {
    return [...areas].sort((a, b) => {
      const nameA = this.i18n.parse(a.objectName).toUpperCase();
      const nameB = this.i18n.parse(b.objectName).toUpperCase();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    });
  }

  public fnLocationCompare(o1: Location, o2: Location): boolean {
    if (!o1 || !o2) return false;
    return o1.objectID === o2.objectID;
  }

  public fnCustomerCompare(o1: string, o2: string): boolean {
    if (!o1 || !o2) return false;
    return o1 === o2;
  }
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function getChildrenLayerNames(locs: Location[], i18n: I18nService): string {
  if (!locs || locs.length === 0) {
    return 'N/A';
  }

  const unique = (value: string, index: number, array: string[]) =>
    array.findIndex((item) => item === value) === index;

  return locs
    .map((item: Location) => i18n.parse(item.layerName))
    .filter(unique)
    .filter((item) => !!item)
    .reduce((a: string, b: string) => a + (a ? '/' : '') + b, '');
}
